import React, { memo } from 'react';
import { Box, Typography, alpha } from '@mui/material';

interface OptionCardProps {
  selected: boolean;
  title: string;
  detail: string;
  onSelect: () => void;
  disabled?: boolean;
}

/** One choice in a small set, shown as a selectable card. */
const OptionCard = memo<OptionCardProps>(({ selected, title, detail, onSelect, disabled }) => (
  <Box
    role="radio"
    aria-checked={selected}
    tabIndex={disabled ? -1 : 0}
    onClick={disabled ? undefined : onSelect}
    onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelect(); } }}
    sx={(theme) => ({
      px: 1.75, py: 1.25, borderRadius: '10px', cursor: disabled ? 'default' : 'pointer', outline: 'none', opacity: disabled ? 0.5 : 1,
      border: '1.5px solid', borderColor: selected ? 'primary.main' : 'divider',
      bgcolor: selected ? alpha(theme.palette.primary.main, 0.06) : 'transparent',
      transition: 'border-color 120ms, background-color 120ms',
      '&:hover': disabled ? undefined : { borderColor: selected ? 'primary.main' : 'text.disabled' },
      '&:focus-visible': { boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.25)}` },
    })}
  >
    <Typography variant="body2" fontWeight={650}>{title}</Typography>
    <Typography variant="caption" color="text.secondary">{detail}</Typography>
  </Box>
));

OptionCard.displayName = 'OptionCard';

export default OptionCard;
